import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import Select from 'react-select';
import { v4 as uuid } from 'uuid';
import { Modal } from 'react-bootstrap';

import startCase from 'lodash/startCase';

import Button from 'shared/button';
import ContactSelector from 'bundles/backoffice/contacts/selector';
import { getPortaledMenuProps, horizontalFormStyles } from 'helpers/react_select_helpers';
import { getSharableAttributes } from './helper';
import { scrollToRef } from 'helpers/scrolling';
import SharedAttributeConfirmation from './shared_confirmation';

const defaultState = {
  askForEmployer: false,
  confirmContactSharing: false,
  confirmEmployerSharing: false,
  contact: null,
  contactSharedAttributes: [],
  employer: null,
  employerChangedByUser: false,
  employerSharedAttributes: [],
  relationship: null,
};

export class GenericContactModal extends React.Component {
  static propTypes = {
    addContactRole: PropTypes.func,
    allowTagging: PropTypes.bool,
    askForEmployer: PropTypes.bool,
    availableContactRelationships: PropTypes.array,
    children: PropTypes.node,
    confirmSharedAttributes: PropTypes.bool,
    contactLabel: PropTypes.string,
    invalid: PropTypes.bool,
    multipleBusinesses: PropTypes.bool,
    newContactVisibility: PropTypes.string,
    parentLabel: PropTypes.string,
    primaryButtonLabel: PropTypes.string,
    requestPermissions: PropTypes.bool,
    show: PropTypes.bool,
    onClose: PropTypes.func,
    onShow: PropTypes.func,
  }

  roleRef = React.createRef();

  static defaultProps = {
    contactLabel: 'contact',
    parentLabel: 'Role',
  }

  state = defaultState;

  componentDidUpdate(previousProps) {
    if (!previousProps.show && this.props.show) {
      this.setState({
        ...defaultState,
        relationship: this.props.availableContactRelationships.length === 1
          ? this.props.availableContactRelationships[0]
          : null,
      });

      if (this.props.onShow) {
        this.props.onShow();
      }
    }
  }

  validateNewContactFormSections = () => {
    if (this.props.askForEmployer) {
      return false;
    }

    if (!this.state.relationship) {
      this.setState({ missingRelationship: true }, () => scrollToRef(this.roleRef));

      // Return an empty array which is truthy, but deal with error rendering ourselves
      return [];
    }
  }

  handleContactChange = (contact, newContact) => {
    this.setState({ contact }, () => {
      if (newContact && !this.props.askForEmployer) {
        this.handleSave();
      }
    });

    if (this.props.askForEmployer && !this.state.employerChangedByUser) {
      this.setState({
        askForEmployer: contact?.isPerson,
        employer: contact?.business,
        employerChangedByUser: false,
      });
    }
  }

  handleEmployerChange = (contact) => {
    this.setState({ employer: contact, employerChangedByUser: true });
  }

  showSharedAttributeConfirmationForContact = (contact) => {
    if (!this.props.confirmSharedAttributes || !contact?.private) {
      return false;
    }

    return getSharableAttributes(contact).length > 0;
  }

  handleRelationshipChange = (relationship) => {
    this.setState({ relationship, missingRelationship: false });
  }

  handleSave = () => {
    const { contact, relationship } = this.state;

    const contactRole = {
      contact,
      employer: this.state.employer,
      // TODO: There is still a mix of relationship and role naming throughout apex/backoffice
      //       Clean up to only use role everywhere
      relationship: relationship.value,
      relationshipLabel: relationship.label,
      role: relationship.value,
      roleLabel: relationship.label,
    };

    if (!contact.id) {
      contact.localId = uuid();
    }

    if (!this.state.confirmContactSharing && this.showSharedAttributeConfirmationForContact(contact)) {
      this.setState({ confirmContactSharing: true });
      return;
    }

    if (!this.state.confirmEmployerSharing && this.showSharedAttributeConfirmationForContact(this.state.employer)) {
      this.setState({ confirmEmployerSharing: true });
      return;
    }

    if (this.state.confirmContactSharing) {
      contactRole.sharedAttributes = this.state.contactSharedAttributes;
    }

    if (this.state.confirmEmployerSharing) {
      contactRole.employer.sharedAttributes = this.state.employerSharedAttributes;
    }

    this.props.addContactRole(contactRole);
    this.props.onClose();
  }

  render() {
    let body;
    if (this.state.confirmEmployerSharing) {
      body = (
        <SharedAttributeConfirmation
          contact={this.state.employer}
          onChange={attributes => this.setState({ employerSharedAttributes: attributes })}
        />
      );
    } else if (this.state.confirmContactSharing) {
      body = (
        <SharedAttributeConfirmation
          contact={this.state.contact}
          onChange={attributes => this.setState({ contactSharedAttributes: attributes })}
        />
      );
    } else {
      body = this.renderForm();
    }

    return (
      <Modal
        backdrop="static"
        className="form-modal visible-overflow contact-modal"
        show={this.props.show}
        onCancel={this.props.onClose}
        onHide={this.props.onClose}
      >
        <Modal.Header closeButton>
          <h3 className="mt-2 mb-0">Add {startCase(this.props.contactLabel)}</h3>
        </Modal.Header>

        <Modal.Body>
          {body}
        </Modal.Body>

        <Modal.Footer>
          {this.renderControls()}
        </Modal.Footer>
      </Modal>
    );
  }

  renderForm() {
    return <>
      {this.renderContactSelector()}
      {this.state.askForEmployer && this.renderEmployerSelector()}

      {this.props.availableContactRelationships.length > 1 &&
        <div className="form-group required">
          <label className="control-label">
            Role
          </label>
          {this.renderRelationshipInput()}
        </div>
      }

      {this.props.children}
    </>;
  }

  renderControls() {
    const disabled = !this.state.contact || !this.state.relationship || this.props.invalid;
    const primaryButtonLabel = this.state.confirmContactSharing
      ? 'Confirm'
      : this.props.primaryButtonLabel;

    return <React.Fragment>
      <Button.Cancel name="cancel" onClick={this.props.onClose} />
      <Button.Primary disabled={disabled} name="save" onClick={this.handleSave}>
        {primaryButtonLabel || `Add ${this.props.contactLabel}`}
      </Button.Primary>
    </React.Fragment>;
  }

  renderContactSelector() {
    let label;
    if (this.state.contact?.isBusiness) {
      label = 'Company*';
    } else if (this.state.contact?.isPerson) {
      label = 'Person*';
    } else {
      label = 'Person or company*';
    }

    return (
      <div className="form-group required">
        <label className="control-label">{label}</label>
        <ContactSelector
          allowTagging={this.props.allowTagging}
          contact={this.state.contact}
          modalBackdrop={false}
          multipleBusinesses={this.props.multipleBusinesses}
          newContactFormSections={this.renderNewContactFormSections()}
          newContactVisibility={this.props.newContactVisibility}
          requestPermissions={this.props.requestPermissions}
          validateNewContactFormSections={this.validateNewContactFormSections}
          onChange={this.handleContactChange}
        />
      </div>
    );
  }

  renderEmployerSelector() {
    return (
      <div className="form-group required">
        <label className="control-label">Company</label>
        <ContactSelector
          allowTagging={this.props.allowTagging}
          contact={this.state.employer}
          contactType="business"
          modalBackdrop={false}
          newContactVisibility={this.props.newContactVisibility}
          requestPermissions={this.props.requestPermissions}
          onChange={this.handleEmployerChange}
        />
      </div>
    );
  }

  renderRelationshipInput() {
    return (
      <Select
        name="relationship"
        options={this.props.availableContactRelationships}
        placeholder="Choose from the dropdown"
        value={this.state.relationship}
        onChange={this.handleRelationshipChange}
      />
    );
  }

  renderNewContactFormSections() {
    if (this.props.askForEmployer) {
      return null;
    }

    return <React.Fragment>
      <h3>{this.props.parentLabel} information</h3>

      {this.state.missingRelationship && (
        <div className="text-danger mb-2">
          Role is required
        </div>
      )}

      <div
        className={classNames('form-group', 'required', { 'form-group-error': this.state.missingRelationship })}
        ref={this.roleRef}
      >
        <label className="control-label">Role</label>
        <div className="form-input-container">
          <Select
            {...getPortaledMenuProps({ ...horizontalFormStyles, error: this.state.missingRelationship })}
            name="relationship"
            options={this.props.availableContactRelationships}
            placeholder="Choose from the dropdown"
            value={this.state.relationship}
            onChange={this.handleRelationshipChange}
          />
        </div>
      </div>
    </React.Fragment>;
  }
}

export default GenericContactModal;
